import { z } from "zod";

export const EXPERIENCE_SCHEMA_VERSION = "ando.experience.v1" as const;

const spanSchema = z.object({
  desktop: z.number().int().min(1).max(12).default(12),
  tablet: z.number().int().min(1).max(8).default(8),
  mobile: z.number().int().min(1).max(4).default(4),
});

const bindingSchema = z.object({
  source: z.string().min(1).max(240),
  contract: z.string().min(1).max(120).optional(),
  label: z.string().max(120).optional(),
  /** Top-level field accepted by the run approval contract for grid edits. */
  editableField: z.string().regex(/^[a-z][a-z0-9_]*$/).max(80).optional(),
});

const actionSchema = z.object({
  kind: z.enum(["approve-run", "reject-run"]),
  confirmation: z.string().max(240).optional(),
  reason: z.string().max(500).optional(),
});

const primitive = z.union([z.string(), z.number(), z.boolean(), z.null()]);

export const experienceComponentSchema = z.object({
  id: z
    .string()
    .regex(/^[a-z][a-z0-9_-]*$/)
    .max(64),
  component: z.enum([
    "metric",
    "status",
    "bar-chart",
    "data-table",
    "editable-grid",
    "key-value",
    "document",
    "pdf-viewer",
    "cad-viewer",
    "action-bar",
    "approval-action",
    "writeback-action",
    "email-action",
    "commission-summary",
    "commission-insights",
    "commission-workspace",
  ]),
  componentVersion: z.literal(1),
  title: z.string().max(120).optional(),
  description: z.string().max(240).optional(),
  span: spanSchema.default({ desktop: 12, tablet: 8, mobile: 4 }),
  bindings: z.record(z.string(), bindingSchema).default({}),
  action: actionSchema.optional(),
  properties: z.record(z.string(), primitive).default({}),
  permissions: z
    .object({
      view: z.array(z.string().min(1)).max(20).default([]),
      interact: z.array(z.string().min(1)).max(20).default([]),
    })
    .optional(),
});

export const experienceManifestSchema = z
  .object({
    schemaVersion: z.literal(EXPERIENCE_SCHEMA_VERSION),
    experienceId: z
      .string()
      .regex(/^[a-z][a-z0-9_-]*$/)
      .max(80),
    revision: z.number().int().positive(),
    flowRevision: z.number().int().positive().optional(),
    contractHash: z.string().max(128).optional(),
    mode: z.enum(["review", "monitor", "workbench", "intake", "operational"]),
    title: z.string().max(160).optional(),
    description: z.string().max(320).optional(),
    components: z.array(experienceComponentSchema).min(1).max(30),
    metadata: z
      .object({
        origin: z.enum(["generated", "user-edited", "template"]),
        status: z.enum(["draft", "published"]).default("draft"),
        generatedAt: z.string().datetime({ offset: true }).optional(),
        publishedAt: z.string().datetime({ offset: true }).optional(),
      })
      .optional(),
  })
  .superRefine((manifest, ctx) => {
    const ids = new Set<string>();
    manifest.components.forEach((component, index) => {
      if (ids.has(component.id)) {
        ctx.addIssue({
          code: "custom",
          path: ["components", index, "id"],
          message: "Component id must be unique",
        });
      }
      ids.add(component.id);
    });
  });

export type ExperienceBinding = z.infer<typeof bindingSchema>;
export type ExperienceAction = z.infer<typeof actionSchema>;
export type ExperienceComponent = z.infer<typeof experienceComponentSchema>;
export type ExperienceManifest = z.infer<typeof experienceManifestSchema>;

export type ManifestResult =
  | { ok: true; manifest: ExperienceManifest }
  | { ok: false; errors: readonly string[] };

/** Parse untrusted graph data. The caller always receives a safe result. */
export function parseExperienceManifest(value: unknown): ManifestResult {
  const parsed = experienceManifestSchema.safeParse(value);
  if (parsed.success) return { ok: true, manifest: parsed.data };
  return {
    ok: false,
    errors: parsed.error.issues.map(
      (issue) => `${issue.path.join(".") || "manifest"}: ${issue.message}`,
    ),
  };
}
